
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import IngredientDisplay from "@/models/Ingredient/IngredientDisplay";
import { NutritionCountry } from "@/models/NutritionLabels/NutritionCountry";
import { mapState } from "vuex";
import VIconWithTooltip from "@/components/vIconWithTooltip.vue";
import {splitLanguages} from "@/models/NutritionLabels/v2/Helpers";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";

@Component({
  components: {VIconWithTooltip},
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
export default class vIngredientsDisplayNames extends Vue {
  localizationState: any;

  @Prop() ingredientDisplays!: IngredientDisplay[];
  @Prop() country!: NutritionCountry;
  @Prop() language!: keyof typeof LabelLanguage;
  @Prop() changedDisplays!: {type: "ingredient" | "subrecipe", id: number}[];

  get languageHeaders() {
    return splitLanguages(LabelLanguage[this.language] as LabelLanguage)
      .map((x) => LabelLanguage[x]);
  }

  // everytime we update the name, tell the parent
  @Watch("activeIngredientId")
  onChangeActiveIngredientId() {
    this.$emit("update");
  }

  activeIngredientId: string = "0";

  setActiveIngredient(id: string) {
    this.activeIngredientId = id;
  }

  get translations() {
    return this.localizationState.translations;
  }

  get sortedIngredientDisplays(): IngredientDisplay[] {
    return [...this.ingredientDisplays].sort(
      (firstDisplay, secondDisplay) => secondDisplay.weightInGrams - firstDisplay.weightInGrams
    );
  }

  getDisplayKey(display: IngredientDisplay, index: number, prefix: string) {
    if (display.ingredientId) {
      return `${prefix}_i_${display.ingredientId}`;
    }

    if (display.recipeId) {
      return `${prefix}_r_${display.recipeId}`;
    }

    return `${prefix}_idx_${index}`;
  }

  /**
   * Emits an event to reset the name of an ingredient or a subrecipe based on the provided display object.
   *
   * @param {IngredientDisplay} display - The object containing ingredient or recipe identification details.
   * @return {void} Emits either 'resetIngredientName' or 'resetSubrecipeName' with the corresponding ID.
   */
  emitResetIngredientName(display: IngredientDisplay) {
    if (display.ingredientId) {
      this.$emit('resetIngredientName', display.ingredientId);
    }

    else {
      this.$emit('resetSubrecipeName', display.recipeId);
    }
  }

  isAiLikeIngredientState(display: IngredientDisplay) {
    return display.allergenVisualState === "ai";
  }

  isManualIngredientState(display: IngredientDisplay) {
    return display.allergenVisualState === "user-added"
      || display.allergenVisualState === "user-confirmed";
  }

  getIngredientStateTooltip(display: IngredientDisplay) {
    switch (display.allergenVisualState) {
    case "ai":
      return "Detected by AI.";
    case "user-confirmed":
    case "user-added":
      return "Manually marked as an allergen.";
    case "user-removed":
      return "Detected by AI, then manually disabled.";
    default:
      return null;
    }
  }

  getIngredientAllergenBadgeVariant(display: IngredientDisplay) {
    if (display.allergenVisualState === "user-removed") {
      return "danger";
    }

    if (this.isAiLikeIngredientState(display)) {
      return "info";
    }

    return "primary";
  }

  onIngredientAllergenChanged(display: IngredientDisplay, isChecked: boolean) {
    display.isAllergen = isChecked;

    const currentState = display.allergenVisualState || "default";

    if (isChecked) {
      if (currentState === "ai") {
        display.allergenVisualState = "ai";
      }
      else if (currentState === "user-removed" || currentState === "user-confirmed") {
        display.allergenVisualState = "user-confirmed";
      }
      else {
        display.allergenVisualState = "user-added";
      }
    }
    else {
      display.allergenVisualState = currentState === "ai"
        || currentState === "user-confirmed"
        ? "user-removed"
        : "default";
    }

    this.onChangeActiveIngredientId();
  }

  isModifiedDisplayName(display: IngredientDisplay) {
    console.log(display);
    if (display.ingredientId) {
      console.log("Changed ingredient?:", this.changedDisplays.some(
        (changedDisplay) => changedDisplay.type === "ingredient" && changedDisplay.id === display.ingredientId
      ));

      return this.changedDisplays.some(
        (changedDisplay) => changedDisplay.type === "ingredient" && changedDisplay.id === display.ingredientId
      );
    }

    console.log("Changed subrecipe?:",this.changedDisplays.some(
      (changedDisplay) => changedDisplay.type === "subrecipe" && changedDisplay.id === display.recipeId
    ));

    return this.changedDisplays.some(
      (changedDisplay) => changedDisplay.type === "subrecipe" && changedDisplay.id === display.recipeId
    );
  }
}
